import { Controller } from "@hotwired/stimulus";

// Drives the "generate / open / download" lifecycle of any record that has a
// `generation_status` column (DocumentAdhesion, DocumentAdherent,
// GenerateDocumentAdherent, DocumentEtude, Avenant, DocumentPhase,
// DocumentIntervenant, DocumentPostulant). Replaces the inline
// `launchDocumentEditorAsync` script that was duplicated across legacy views.
//
// Usage in ERB:
//   <div data-controller="doc-generator"
//        data-doc-generator-junior-id-value="<%= junior.id %>"
//        data-doc-generator-doc-id-value="<%= doc.id %>"
//        data-doc-generator-doc-class-value="GenerateDocumentAdherent"
//        data-doc-generator-status-value="<%= doc.generation_status %>">
//     <button data-doc-generator-target="launchBtn"
//             data-action="click->doc-generator#launch">…</button>
//   </div>
//
// Behavior:
//   - On `launch` click → confirm, POST to /juniors/:id/generated_documents,
//     swap the button to a spinner, then poll /status until done|failed.
//   - If the controller mounts on an element whose status is already
//     "processing", it auto-resumes polling (covers page reloads).
export default class extends Controller {
  static values = {
    juniorId:    Number,
    docId:       Number,
    docClass:    String,
    status:      { type: String, default: "idle" },
    confirmText: { type: String, default: "Lancer la génération de ce document ?" },
    pollInterval: { type: Number, default: 3000 }
  };
  static targets = ["launchBtn"];

  connect() {
    if (this.statusValue === "processing") this._startPolling();
  }

  disconnect() {
    this._stopPolling();
  }

  launch(event) {
    event.preventDefault();
    if (!window.confirm(this.confirmTextValue)) return;
    this._setLoading();

    fetch(`/juniors/${this.juniorIdValue}/generated_documents`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      body: JSON.stringify({
        generated_document: {
          document_class: this.docClassValue,
          document_id:    this.docIdValue
        }
      })
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.generation_status === "started") {
          this._startPolling();
        } else {
          window.alert(data.error || "Erreur de lancement de la génération.");
          window.location.reload();
        }
      })
      .catch(() => {
        window.alert("Erreur réseau pendant le lancement de la génération.");
        window.location.reload();
      });
  }

  _startPolling() {
    if (this._pollHandle) return;
    this._pollHandle = setInterval(() => this._poll(), this.pollIntervalValue);
  }

  _stopPolling() {
    if (this._pollHandle) {
      clearInterval(this._pollHandle);
      this._pollHandle = null;
    }
  }

  _poll() {
    fetch(`/juniors/${this.juniorIdValue}/generated_documents/${this.docIdValue}/status?klass=${this.docClassValue}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.generation_status === "done") {
          this._stopPolling();
          window.location.reload();
        } else if (data.generation_status === "failed") {
          this._stopPolling();
          window.alert("La génération a échoué. Réessaie depuis le document.");
          window.location.reload();
        }
      })
      .catch(() => this._stopPolling());
  }

  _setLoading() {
    if (!this.hasLaunchBtnTarget) return;
    this.launchBtnTarget.disabled = true;
    this.launchBtnTarget.innerHTML =
      '<i class="fa-solid fa-spinner fa-spin"></i> Génération…';
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }
}
